import { PageTitle as SharedPageTitle } from '../components/layout/PageHeader';
/**
 * Data Entry Page
 * Unified page with tabbed interface for all data entry methods:
 * - Invoice Scanning with AI
 * - Manual Entry
 * - Sales Data
 */

import React, { useState } from 'react';
import styled from 'styled-components';

// Import tab components
import { ServiceConsumptionTab, SalesDataTab } from '../components/tabs';

// ============================================================================
// Types
// ============================================================================

type TabId = 'service-consumption' | 'sales-data';

interface Tab {
  id: TabId;
  label: string;
  description: string;
}

// ============================================================================
// Styled Components
// ============================================================================

const PageContainer = styled.div`
  width: 100%;
  min-width: 0;
  max-width: none;
  margin: 0;
  padding: 0;
`;

const PageHeader = styled.div`
  text-align: left;
  margin-bottom: 1.5rem;
`;

const PageTitle = styled(SharedPageTitle)``;

const PageDescription = styled.p`
  margin: 0.5rem 0 0;
  max-width: 65ch;
  font-size: 0.875rem;
  line-height: 1.6;
  color: var(--text-muted);
`;

const TabNavigation = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
`;

const TabButton = styled.button<{ $isActive: boolean }>`
  padding: 0.625rem 1.25rem;
  border: 1px solid
    ${(props) =>
      props.$isActive ? 'var(--action-primary)' : 'var(--border-strong)'};
  border-radius: var(--radius-md);
  background: ${(props) =>
    props.$isActive ? 'var(--action-primary)' : 'var(--surface)'};
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  color: ${(props) =>
    props.$isActive ? 'var(--on-action)' : 'var(--text-primary)'};
  cursor: pointer;
  transition: background-color var(--transition-fast);
  position: relative;
  white-space: nowrap;
  min-height: 44px; /* Touch target */

  &:hover {
    background: ${(props) =>
      props.$isActive
        ? 'var(--action-primary-hover)'
        : 'var(--interactive-hover)'};
  }

  &:focus-visible {
    outline: 2px solid var(--interactive-focus);
    outline-offset: 3px;
  }

  /* Mobile devices - enhanced touch targets */
  @media (max-width: 767px) {
    padding: var(--space-3) var(--space-5);
    font-size: var(--font-size-sm);
    min-height: 48px; /* Enhanced touch target for mobile */
  }
`;

const TabContent = styled.div`
  min-height: 60vh;
`;

const TabDescription = styled.div`
  margin-bottom: 1.5rem;
  max-width: 65ch;
`;

const TabDescriptionText = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: var(--text-secondary);
  line-height: 1.6;
`;

// Content wrapper to ensure consistent styling across tabs
const TabContentWrapper = styled.div`
  /* Override page containers within tabs to prevent double padding */
  > div[class*='PageContainer'] {
    max-width: none;
    margin: 0;
    padding: 0;
  }

  /* Ensure forms and components maintain proper spacing */
  > div > div:first-child {
    margin-top: 0;
  }
`;

// ============================================================================
// Component
// ============================================================================

const tabs: Tab[] = [
  {
    id: 'service-consumption',
    label: 'Consumos',
    description:
      'Registra tus datos de consumo de electricidad, agua o gas mediante escaneo de facturas o entrada manual.',
  },
  {
    id: 'sales-data',
    label: 'Ventas y cubiertos',
    description:
      'Registra el número de clientes y cubiertos para calcular métricas por persona.',
  },
];

export const DataEntryPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>('service-consumption');

  const handleTabChange = (tabId: TabId) => {
    setActiveTab(tabId);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'service-consumption':
        return (
          <TabContentWrapper>
            <ServiceConsumptionTab />
          </TabContentWrapper>
        );
      case 'sales-data':
        return (
          <TabContentWrapper>
            <SalesDataTab />
          </TabContentWrapper>
        );
      default:
        return null;
    }
  };

  const activeTabInfo = tabs.find((tab) => tab.id === activeTab);

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Añadir datos</PageTitle>
        <PageDescription>
          Selecciona un método para registrar tus datos de consumo o ventas.
        </PageDescription>
      </PageHeader>

      <TabNavigation role="tablist" aria-label="Métodos de ingreso de datos">
        {tabs.map((tab) => (
          <TabButton
            key={tab.id}
            $isActive={activeTab === tab.id}
            onClick={() => handleTabChange(tab.id)}
            type="button"
            tabIndex={activeTab === tab.id ? 0 : -1}
            onKeyDown={(event) => {
              const index = tabs.findIndex((item) => item.id === tab.id);
              const next =
                event.key === 'Home'
                  ? 0
                  : event.key === 'End'
                    ? tabs.length - 1
                    : event.key === 'ArrowRight'
                      ? (index + 1) % tabs.length
                      : event.key === 'ArrowLeft'
                        ? (index + tabs.length - 1) % tabs.length
                        : -1;
              const nextTab = tabs[next];
              if (nextTab) {
                event.preventDefault();
                handleTabChange(nextTab.id);
                document.getElementById(`tab-${nextTab.id}`)?.focus();
              }
            }}
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`tabpanel-${tab.id}`}
            id={`tab-${tab.id}`}
          >
            {tab.label}
          </TabButton>
        ))}
      </TabNavigation>

      {activeTabInfo && (
        <TabDescription>
          <TabDescriptionText>{activeTabInfo.description}</TabDescriptionText>
        </TabDescription>
      )}

      <TabContent
        role="tabpanel"
        id={`tabpanel-${activeTab}`}
        aria-labelledby={`tab-${activeTab}`}
      >
        {renderTabContent()}
      </TabContent>
    </PageContainer>
  );
};

DataEntryPage.displayName = 'DataEntryPage';
